import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card, Button, Alert, Spinner, Badge } from 'react-bootstrap'
import { chatsAPI } from '../../services/chatsApi'
import { getConsumer } from '../../services/cable'
import ChatSectionHeader from './ChatSectionHeader'
import InviteUsersModal from './InviteUsersModal'

const ChatHub = ({ currentUser }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [chat, setChat] = useState(null)
  const [members, setMembers] = useState([])
  const [stages, setStages] = useState([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showInviteModal, setShowInviteModal] = useState(false)

  const loadChat = useCallback(async () => {
    try {
      setLoading(true)
      const response = await chatsAPI.get(id)
      setChat(response.data)
      setMembers(response.data.members)
      setStages(response.data.stages)
      setUnread(response.data.unread_count)
      setError(null)
    } catch (err) {
      console.error('Error loading chat:', err)
      setError(err.response?.status === 403 ? 'У вас нет доступа к этому чату' : 'Не удалось загрузить чат')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadChat()
  }, [loadChat])

  useEffect(() => {
    const subscription = getConsumer().subscriptions.create(
      { channel: 'ChatChannel', chat_id: id },
      {
        received: (data) => {
          if (data.type === 'message') {
            if (!currentUser || data.message.user.id !== currentUser.id) {
              setUnread((prev) => prev + 1)
            }
          } else if (data.type === 'members') {
            setMembers(data.members)
          }
        },
      }
    )

    return () => subscription.unsubscribe()
  }, [id, currentUser])

  const handleDeleteChat = async () => {
    if (!window.confirm('Удалить чат вместе со всеми сообщениями?')) return

    try {
      await chatsAPI.delete(id)
      window.dispatchEvent(new Event('chats:updated'))
      navigate('/chats')
    } catch (err) {
      console.error('Error deleting chat:', err)
      setError('Не удалось удалить чат')
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    )
  }

  if (error && !chat) {
    return <Alert variant="danger">{error}</Alert>
  }

  const canDelete = currentUser && (currentUser.roles?.includes('admin') || chat.creator_id === currentUser.id)

  return (
    <div>
      <ChatSectionHeader
        title={chat.name}
        subtitle={`Проект: ${chat.project_address}`}
        backTo="/chats"
        actions={
          <>
            <Button variant="outline-primary" size="sm" onClick={() => setShowInviteModal(true)}>
              Пригласить
            </Button>
            {canDelete && (
              <Button variant="outline-danger" size="sm" onClick={handleDeleteChat}>
                Удалить
              </Button>
            )}
          </>
        }
      />

      <div className="mb-3">
        {members.map((m) => (
          <Badge key={m.id} bg="light" text="dark" className="me-1 mb-1 border">
            {m.email}
          </Badge>
        ))}
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Card className="shadow-sm border-0">
        <div className="list-group list-group-flush">
          <Link to={`/chats/${id}/messages`} className="list-group-item list-group-item-action py-3">
            <div className="d-flex justify-content-between align-items-center gap-2">
              <span className="fw-semibold text-uppercase">Чат</span>
              <span className="d-flex align-items-center gap-2">
                {unread > 0 && (
                  <Badge bg="danger" pill>
                    {unread}
                  </Badge>
                )}
                <span className="text-muted">›</span>
              </span>
            </div>
          </Link>

          {stages.map((stage) => (
            <Link
              key={stage.id}
              to={`/chats/${id}/stages/${stage.id}`}
              className="list-group-item list-group-item-action py-3"
            >
              <div className="d-flex justify-content-between align-items-center gap-2">
                <span className="flex-grow-1" style={{ minWidth: 0 }}>
                  <span className="fw-semibold text-truncate d-block">
                    {stage.completed && <span className="text-success me-1">✓</span>}
                    Этап {stage.number}. {stage.name}
                  </span>
                  <span className="text-muted small">
                    {stage.tab_type === 'additional' && stage.tab_name ? `${stage.tab_name} · ` : ''}
                    Задачи: {stage.completed_tasks_count} из {stage.tasks_count} · Акты: {stage.acts_count}
                    {stage.unpaid_acts_count > 0 && ` (не оплачено: ${stage.unpaid_acts_count})`}
                  </span>
                </span>
                <span className="text-muted flex-shrink-0">›</span>
              </div>
            </Link>
          ))}
        </div>
      </Card>

      {stages.length === 0 && (
        <Alert variant="light" className="border text-center text-muted mt-3">
          В проекте пока нет этапов работ.
        </Alert>
      )}

      <InviteUsersModal
        show={showInviteModal}
        onHide={() => setShowInviteModal(false)}
        chatId={id}
        onInvited={setMembers}
      />
    </div>
  )
}

export default ChatHub
